// The derived, per-site browser-origin label (per-site-origins-plan.md "The scheme"). Lives here, not in
// @szzt/server, because it must be identical in two runtimes: the Node gateway (host classification,
// packages/server/src/apex.ts parseHost()) and the browser publisher (apps/publisher/src/main.ts
// atIdentityUrl(), per-site-origins-plan.md Chunk 4) — one implementation, not a hand-copied second one.
// This module is browser-pure (no `node:` import) and dependency-free, so it inlines cleanly into the
// publisher bundle via @szzt/core.
//
// The label ENCODES the identity reversibly: `<siteRkey>--<did-plc-suffix>`. It is NOT a hash. A hashed
// label was one-way, so the gateway could not recover the identity from the host and the path had to
// repeat it (`/at/<did>/<rkey>/`) — the identity appearing twice, and the apex `/at/` route executing
// author JS on the same origin as the publisher's R2 credentials and OAuth session. Because the label is
// reversible, the `/at/` path route is deleted outright (Chunks 2/5) rather than redirected.
//
// Reachability (szzt-spec.md §11.3) survives because the label is DERIVED, not allocated: any
// gateway computes it from (did, siteRkey) with no index, so it cannot be withheld and it is identical
// across gateways.
//
// did:web authors are knowingly excluded from identity addressing — a did:web contains dots, which
// cannot live inside a single DNS label. They use a slug (§11.1) or a custom domain (§11.2). This is a
// deliberate, operator-accepted limitation, not a TODO.

/** Max length of a single DNS label (RFC 1035). The whole `<rkey>--<suffix>` label must fit. */
const MAX_DNS_LABEL = 63;

const DID_PLC_PREFIX = "did:plc:";

// The did:plc identifier suffix as it must appear inside a DNS label: lowercase alphanumeric, no
// hyphen. Real did:plc suffixes are 24 chars of lowercase base32-sortable, so this admits every real
// one; it is deliberately no stricter than round-tripping requires, because over-strictness here would
// make a site UNREACHABLE at its identity origin (§11.3), which is worse than admitting a shape plc
// does not currently mint. Two properties are load-bearing and both are enforced here:
//   - no uppercase — DNS labels are case-insensitive, so an uppercase suffix could not round-trip;
//   - no hyphen — this is what makes the `--` separator unambiguous (see parseSiteOriginLabel).
const DID_PLC_SUFFIX = /^[a-z0-9]+$/;

// The record key as it must appear inside a DNS label. atproto record keys are case-SENSITIVE and allow
// `a-zA-Z0-9.-_:~` (@atproto/syntax recordkey.js: `RECORD_KEY_REGEX = /^[a-zA-Z0-9_~.:-]{1,512}$/`).
// DNS labels are case-INSENSITIVE and allow only `[a-z0-9-]`. The intersection is what can round-trip:
// lowercase alphanumerics plus single internal hyphens. Everything else is REJECTED (null), never
// encoded and never silently folded — if `Foo` and `foo` are distinct record keys but the same DNS
// label, a request could resolve to the WRONG SITE. That is a correctness bug, not an edge case, so the
// honest answer is to refuse to address such a site by identity rather than to invent a codec.
// In practice `app.szzt.site` rkeys are TIDs (13 chars of `[a-z2-7]`), which always pass.
// The shape also forbids a leading/trailing hyphen (illegal in a DNS label) and any `--` inside the
// rkey — see parseSiteOriginLabel for why that matters.
const SITE_RKEY_IN_LABEL = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** The separator. Load-bearing twice over — see parseSiteOriginLabel and the disjointness note below. */
const SEP = "--";

// Namespace disjointness, provable and unchanged by the move off hashing (per-site-origins-plan.md
// "Namespace disjointness"):
//   - vs slugs: `packages/moderation/src/slugs.ts validateSlug()` rejects ANY slug containing `--`, so
//     every label in this namespace is structurally unallocatable as a slug. This does not depend on the
//     old `site--` prefix — it depends only on the separator, which every label still contains.
//   - vs version-pinned CID origins: `packages/server/src/apex.ts looksLikeCid()` is
//     `/^baf[yk][a-z2-7]{20,}$/`, anchored, and `-` is not in `[a-z2-7]`. Every label here contains `-`,
//     so no label can ever match.
// Both are property-tested in packages/server/test/apex.test.ts.

/**
 * The derived, per-site browser-origin label: `<siteRkey>--<did-plc-suffix>`, e.g.
 * `3mq7agh45cd2n--dctnumslt4gj6ykl3vhiue5x`. A binding, not a name — no durable state, no lookup.
 *
 * Encodes the DID, never the handle (the DID is the stable identity; the handle can change, §11.7).
 *
 * Returns `null` — never throws, never silently falls back — when the pair cannot round-trip through a
 * case-insensitive DNS label: a non-`did:plc` identity (including every did:web), a record key holding
 * uppercase or any of `. _ : ~`, a record key with a leading/trailing hyphen or an internal `--`, or a
 * label that would exceed the 63-char DNS bound. Callers MUST handle null; such a site is reachable via
 * a slug (§11.1) or a custom domain (§11.2), just not by identity.
 */
export function siteOriginLabel(did: string, siteRkey: string): string | null {
  if (!did.startsWith(DID_PLC_PREFIX)) return null;
  const suffix = did.slice(DID_PLC_PREFIX.length);
  if (!DID_PLC_SUFFIX.test(suffix)) return null;
  if (!SITE_RKEY_IN_LABEL.test(siteRkey)) return null;

  const label = `${siteRkey}${SEP}${suffix}`;
  if (label.length > MAX_DNS_LABEL) return null;
  return label;
}

/**
 * The reverse of {@link siteOriginLabel}: recover `(did, siteRkey)` from a label, or `null`. This is
 * what lets the gateway resolve a site from the host alone, with no `/at/` path and no index.
 *
 * Exact and total against `siteOriginLabel`: this accepts a label if and only if `siteOriginLabel`
 * would have produced it, and the round-trip is the identity in both directions. Anything looser would
 * let a host resolve to an identity that could never have been addressed by it.
 *
 * On splitting: the label is split at the FIRST `--`, but the choice is immaterial rather than a
 * convention to remember. A record key can legally contain `--` (`-` is in atproto's record-key
 * charset), which is exactly why {@link SITE_RKEY_IN_LABEL} forbids it, and a did:plc suffix contains no
 * hyphen at all. So a well-formed label contains EXACTLY ONE `--`, and first == last. A label with a
 * second `--` (e.g. `a--b--c`) is rejected whichever end you split from, because strict validation of
 * both halves fails: split-first leaves the suffix holding a hyphen, split-last leaves the rkey holding
 * `--`. Both directions are asserted in packages/core/test/site-origin.test.ts.
 */
export function parseSiteOriginLabel(label: string): { did: string; siteRkey: string } | null {
  if (label.length > MAX_DNS_LABEL) return null;

  const at = label.indexOf(SEP);
  if (at === -1) return null;

  const siteRkey = label.slice(0, at);
  const suffix = label.slice(at + SEP.length);
  if (!SITE_RKEY_IN_LABEL.test(siteRkey)) return null;
  if (!DID_PLC_SUFFIX.test(suffix)) return null;

  return { did: `${DID_PLC_PREFIX}${suffix}`, siteRkey };
}

/**
 * True when a subdomain label is shaped like a derived per-site origin. Defined as "parses", so it can
 * never drift out of agreement with {@link parseSiteOriginLabel} — the classifier in
 * `packages/server/src/apex.ts parseHost()` and the resolver must agree on exactly one label space.
 */
export function looksLikeSiteOrigin(label: string): boolean {
  return parseSiteOriginLabel(label) !== null;
}
